import React, { useState, useEffect } from "react";
import { Input, Button, Tooltip, Progress, Upload, message } from "antd";
import { ClockCircleOutlined, CloudUploadOutlined, DeleteOutlined, EditOutlined, PictureOutlined } from "@ant-design/icons";
import './VideoItem.css';
import axiosInstance from "../../Config/axiosConfig"
import { uploadVideoInChunks } from "../../Utilities/videoChunkUploader";

const VideoItem = ({ 
  video, 
  handleInputChange, 
  handleRemoveVideo, 
  handleThumbnailUpload,
  onUploadComplete,
  isUploadingAll,
  isCurrentUpload,
  uploadDisabled
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(video.title || video.file?.name || ""); 
  const [isUploading, setIsUploading] = useState(false); 
  const [isDeleting, setIsDeleting] = useState(false); 
  const [isUpdating, setIsUpdating] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [uploadController, setUploadController] = useState(null);
  const [lastFailedChunks, setLastFailedChunks] = useState(null);

  const formatTitle = (title) => {
    return title
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Auto-start upload when this is the current video in sequence
  useEffect(() => {
    if (isCurrentUpload && !video.isUploaded && video.file && !isUploading) {
      console.log(`Auto-starting upload for video: ${video.title}`);
      handleUploadClick();
    }
  }, [isCurrentUpload, video.isUploaded, video.file]);

  useEffect(() => {
    // Set initial title from file name if available
    if (video.file?.name && !video.title) {
      const fileNameWithoutExtension = video.file.name.replace(/\.[^/.]+$/, "");
      const formattedTitle = formatTitle(fileNameWithoutExtension);
      setInputValue(formattedTitle);
      handleInputChange(video.videoId, "title", formattedTitle);
    } else if (video.title) {
      const formattedTitle = formatTitle(video.title);
      setInputValue(formattedTitle);
    }
  }, [video.file?.name, video.title, video.videoId, handleInputChange]);

  useEffect(() => {
    if (video.isUploaded) {
      setUploadStatus('completed');
    } else if (video.uploadProgress > 0 && video.uploadProgress < 100) {
      setUploadStatus('uploading');
    } else {
      setUploadStatus('idle');
    }
  }, [video.uploadProgress, video.isUploaded]);

  const handleInputValueChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    handleInputChange(video.videoId, "title", newValue);
  };

  useEffect(() => {
    return () => {
      if (video.thumbnail instanceof File) {
        URL.revokeObjectURL(video.thumbnail);
      }
      if (video.file instanceof File) {
        URL.revokeObjectURL(video.file);
      }
    };
  }, [video.thumbnail, video.file]);

  const toggleEditing = () => {
    if (isEditing) {
      handleUpdateVideo();
    } else {
      setIsEditing(true);
    }
  };

  const handleUpdateVideo = async (thumbnailFile = null) => {
    if (!inputValue.trim()) {
      message.error("Please enter a valid title for the video.");
      return;
    }

    const formattedTitle = formatTitle(inputValue);
    setInputValue(formattedTitle);
    
    setIsUpdating(true);
    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");

    try {
      const formData = new FormData();
      formData.append("id", video.videoId);
      formData.append("title", formattedTitle);
      formData.append("is_intro", video.isIntro || false);
      
      const thumbnailToUpload = thumbnailFile || video.thumbnail;
      if (thumbnailToUpload instanceof File) {
        formData.append("thumbnail", thumbnailToUpload);
      }

      const response = await axiosInstance.post("/Video/update",
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("Video details updated successfully!");
        handleInputChange(video.videoId, "title", formattedTitle);
        handleInputChange(video.videoId, "updatedAt", new Date().toISOString());
        if (thumbnailToUpload instanceof File) {
          handleInputChange(video.videoId, "thumbnailUploaded", true);
        }
        setIsEditing(false);
      } else {
        throw new Error(response.data.message || "Failed to update video details");
      }
    } catch (error) {
      console.error("Update Error:", error);
      message.error(error.message || "Error updating video. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUploadClick = async () => {
    if (!video.file || !video.videoId) {
      message.error("No video file selected or missing video ID");
      return;
    }

    setIsUploading(true);
    const controller = new AbortController();
    setUploadController(controller);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const storedDeviceId = localStorage.getItem("deviceId");
      const storedUserIp = localStorage.getItem("userIp");

      const result = await uploadVideoInChunks(
        video.file,
        video.videoId,
        accessToken,
        storedDeviceId,
        storedUserIp,
        (progress) => {
          handleInputChange(video.videoId, "uploadProgress", progress);
        },
        video.isIntro,
        controller.signal,
        (status) => {
          setUploadStatus(status);
        },
        lastFailedChunks 
      );
  
      if (result.success) {
        handleInputChange(video.videoId, "isUploaded", true);
        message.success("Video uploaded successfully!");
        
        // Notify parent component about upload completion
        if (onUploadComplete) {
          onUploadComplete(video.videoId, true);
        }
      } else if (result.status === 'paused') {
        message.warning(`Upload paused with ${result.failedChunks.length} failed chunks. Click upload to retry.`);
        
        // If this was part of a sequence, notify failure
        if (onUploadComplete && isUploadingAll) {
          onUploadComplete(video.videoId, false);
        }
      } else {
        throw new Error(result.message || "Upload failed");
        
        // If this was part of a sequence, notify failure
        if (onUploadComplete && isUploadingAll) {
          onUploadComplete(video.videoId, false);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        message.info("Upload cancelled");
        
        // If this was part of a sequence, notify failure
        if (onUploadComplete && isUploadingAll) {
          onUploadComplete(video.videoId, false);
        }
      } else {
        console.error("Upload Error:", error);
        message.error(error.message || "Upload failed. Please check your connection and try again.");
        
        // If this was part of a sequence, notify failure
        if (onUploadComplete && isUploadingAll) {
          onUploadComplete(video.videoId, false);
        }
      }
    } finally {
      setIsUploading(false);
      setUploadController(null);
    }
  };

  const handleCancelUpload = () => {
    if (uploadController) {
      uploadController.abort();
      setUploadStatus('paused');
      
      // If this was part of a sequence, notify failure
      if (onUploadComplete && isUploadingAll) {
        onUploadComplete(video.videoId, false);
      }
    }
  };

  const getUploadButtonText = () => {
    if (isCurrentUpload && isUploadingAll) {
      return 'Uploading...';
    }
    
    switch (uploadStatus) {
      case 'completed': return 'Uploaded';
      case 'uploading': return 'Uploading...';
      case 'paused': 
        return video.uploadProgress > 0 ? 'Resume Upload' : 'Upload Video';
      default: return 'Upload Video';
    }
  };

  const handleDeleteVideo = async () => {
    if (!video.videoId) {
      message.error("Video ID is missing. Cannot delete video.");
      return;
    }

    setIsDeleting(true);
    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");

    try {
      const response = await axiosInstance.get(`/Video/delete/${video.videoId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );
      
      if (response.data.code === 200) {
        message.success("Video deleted successfully!");
        handleRemoveVideo(video.videoId);
      } else {
        throw new Error(response.data.message || "Failed to delete video");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      message.error(error.message || "Error deleting video. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleThumbnailChange = (file) => {
    const isValidType = file.type === "image/png" || file.type === "image/jpeg";
    if (!isValidType) {
      message.error("Only PNG and JPG files are allowed for thumbnails.");
      return false;
    }
    
    const isSameFile = video.thumbnail instanceof File && 
      file.name === video.thumbnail.name && 
      file.size === video.thumbnail.size;
    
    if (isSameFile) {
      message.warning("This thumbnail is already in use");
      return false;
    }
    
    handleThumbnailUpload(video.videoId, file);
    handleUpdateVideo(file); 
    return false;
  };

  // Determine button disabled state
  const isUploadButtonDisabled = () => {
    if (uploadStatus === 'completed') return true;
    if (isDeleting) return true;
    if (uploadDisabled) return true;
    if (isUploadingAll && !isCurrentUpload) return true;
    return false;
  };

  return (
    <div className="video-item" style={{
      opacity: uploadDisabled ? 0.6 : 1,
      borderLeft: isCurrentUpload ? '4px solid #1890ff' : 'none'
    }}>
      <div className="video-processing">
        <ClockCircleOutlined style={{ fontSize: "24px" }} />
        <p className="video-processing-text">Video Processing</p>
      </div>

      {isCurrentUpload && isUploadingAll && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: '#1890ff',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          Uploading Now
        </div>
      )}

      <div className="video-item-actions">
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
          <Input
            placeholder="Enter video topic title"
            value={inputValue}
            onChange={handleInputValueChange}
            style={{ width: "250px", height: "40px" }}
            disabled={!isEditing || isUploadingAll}
          />
          {isEditing ? (
            <Button
              type="primary"
              onClick={() => handleUpdateVideo()}
              style={{ width: "80px", height: "40px" }}
              loading={isUpdating}
              disabled={isUploadingAll}
            >
              Save
            </Button>
          ) : (
            <Button
              icon={<EditOutlined />}
              onClick={toggleEditing}
              style={{ width: "40px", height: "40px" }}
              disabled={isUploadingAll}
            />
          )}

          {video.isIntro && (
            <Upload
              accept="image/png, image/jpeg, image/jpg"
              beforeUpload={handleThumbnailChange}
              showUploadList={false}
              disabled={isUploadingAll}
            >
              <Tooltip title="Upload Thumbnail" placement="top">
                <Button
                  icon={<PictureOutlined style={{ fontSize: "20px" }} />}
                  style={{ width: "40px", height: "40px" }}
                  disabled={isUploadingAll}
                />
              </Tooltip>
            </Upload>
          )}
          
          <Button
            icon={<DeleteOutlined />}
            onClick={handleDeleteVideo} 
            style={{ width: "40px", height: "40px" }}
            loading={isDeleting}
            disabled={isDeleting || isUploadingAll}
          />

          <Button
            icon={<CloudUploadOutlined />}
            onClick={uploadStatus === 'uploading' ? handleCancelUpload : handleUploadClick}
            style={{ width: "150px", height: "40px" }}
            disabled={isUploadButtonDisabled()}
            loading={isUploading}
          >
            {getUploadButtonText()}
          </Button>
        </div>

        <Progress
          percent={video.uploadProgress}
          status={video.uploadProgress < 100 ? "active" : "success"}
          strokeColor={video.uploadProgress < 100 ? "#1890ff" : "#52c41a"}
          showInfo={false}
          style={{ marginTop: "8px", width: "100%" }}
        />
      </div>
    </div>
  );
};

export default VideoItem;